import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import {
  EuiDatePicker,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormRow,
  EuiLoadingSpinner,
  EuiSelect,
  EuiSpacer,
  EuiText,
  EuiToolTip,
} from '@elastic/eui';
import GetDailyVerse from './DailyVerse';
import { TranslateLanguage as translateLanguageText } from './KeywordTranslator';
import '../qt-history.css';

const DAY_IN_MS = 24 * 60 * 60 * 1000;

const toLocalDateKey = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatShortDate = date => {
  const [, month, day] = String(date).split('-');
  return month && day ? `${Number(month)}/${Number(day)}` : date;
};

const getDayIndex = dateKey => {
  const [year, month, day] = String(dateKey).split('-').map(Number);
  if (!year || !month || !day) return 0;
  const index = Math.floor(
    (Date.UTC(year, month - 1, day) - Date.UTC(year, 0, 1)) / DAY_IN_MS
  );
  return Math.max(0, Math.min(index, 364));
};

const dateKeyFromDayIndex = (year, dayIndex) => {
  const date = new Date(Date.UTC(year, 0, dayIndex + 1));
  return date.toISOString().slice(0, 10);
};

const normalizeDaily = value => {
  if (!value) return { book: '', id: '', link: '' };
  if (typeof value === 'string') return { book: value, id: '', link: '' };
  if (Array.isArray(value)) {
    const firstWithLink = value.find(item => item && typeof item === 'object' && item.link);
    return {
      book: value
        .map(item => (
          typeof item === 'string'
            ? item
            : [item?.book, item?.id].filter(Boolean).join(' ')
        ))
        .join('；'),
      id: value.map(item => item?.id).filter(Boolean).join('；'),
      link: firstWithLink?.link || '',
    };
  }
  return {
    book: value.book || value.title || '',
    id: value.id || '',
    link: value.link || '',
  };
};

async function fetchYTLinkByQuery(query, YTKey) {
  if (!YTKey) return '';
  const api = `https://www.googleapis.com/youtube/v3/search?part=snippet&type=video&maxResults=1&q=${encodeURIComponent(query)}&key=${YTKey}`;
  const response = await fetch(api);
  const result = await response.json();
  const videoId = result?.items?.[0]?.id?.videoId;
  return videoId ? `https://www.youtube.com/watch?v=${videoId}` : '';
}

export default function YearReadModal({
  initialPlan = 'HT',
  TranslateLanguage,
  YTKey,
}) {
  const translate = text => (
    (TranslateLanguage || translateLanguageText)(localStorage.userLanguage, text)
  );
  const coercePlan = planValue => (
    ['HT', 'OT', 'NT'].includes(planValue) ? planValue : 'HT'
  );
  const today = toLocalDateKey();
  const currentYear = Number(today.slice(0, 4));
  const todayIndex = getDayIndex(today);
  const [plan, setPlan] = useState(coercePlan(initialPlan));
  const [selectedDate, setSelectedDate] = useState(today);
  const [url, setUrl] = useState('');
  const [bookName, setBookName] = useState('');
  const [dayNumber, setDayNumber] = useState(todayIndex + 1);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const planOptions = [
    { value: 'HT', text: translate('時序') },
    { value: 'OT', text: translate('舊約開始') },
    { value: 'NT', text: translate('新約開始') },
  ];

  const selectReading = async (nextPlan, dateKey) => {
    try {
      setLoading(true);
      setMessage('');
      const dayIndex = getDayIndex(dateKey);
      const { book, id, link } = normalizeDaily(GetDailyVerse(dayIndex, nextPlan));
      let finalLink = link;

      if (!finalLink) {
        const query = [book, id, translate('聖經朗讀')].filter(Boolean).join(' ');
        finalLink = await fetchYTLinkByQuery(query, YTKey);
      }

      setSelectedDate(dateKey);
      setDayNumber(dayIndex + 1);
      setBookName(book || id || '');
      setUrl(finalLink || '');
      if (!finalLink) setMessage(translate('抱歉，沒有找到可播放的內容。'));
    } catch (error) {
      console.error('Failed to load year reading:', error);
      setSelectedDate(dateKey);
      setUrl('');
      setMessage(translate('讀取失敗，請稍後再試。'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.body.classList.add('qt-player-modal-open');
    selectReading(coercePlan(initialPlan), today);
    return () => document.body.classList.remove('qt-player-modal-open');
  }, []);

  const iframeSrc = useMemo(() => {
    if (!url) return '';
    const match = url.match(/(?:https?:\/{2})?(?:w{3}\.)?youtu(?:be)?\.(?:com|be)(?:\/watch\?v=|\/)([^\s&]+)/);
    if (!match || !match[1]) return '';
    const parsed = new URL(url, window.location.origin);
    const startTime = parsed.searchParams.get('t');
    return `https://www.youtube.com/embed/${match[1]}?autoplay=1${startTime ? `&start=${startTime}` : ''}`;
  }, [url]);

  const selectedIndex = getDayIndex(selectedDate);
  const previousDate = selectedIndex > 0
    ? dateKeyFromDayIndex(currentYear, selectedIndex - 1)
    : '';
  const nextDate = selectedIndex < 364
    ? dateKeyFromDayIndex(currentYear, selectedIndex + 1)
    : '';
  const recentDates = Array.from(
    { length: Math.min(7, todayIndex + 1) },
    (_, offset) => dateKeyFromDayIndex(currentYear, todayIndex - offset)
  );

  return (
    <div className="qt-player-modal-content year-read-modal-content">
      <EuiFlexGroup
        className="qt-player-source-row"
        justifyContent="center"
        alignItems="center"
        gutterSize="s"
        responsive={false}
      >
        <EuiFlexItem grow={false}>
          <div className="year-read-plan-row">
            <label className="year-read-plan-label" htmlFor="year-read-plan-select">
              {translate('讀經計劃')}
            </label>
            <EuiSelect
              compressed
              options={planOptions}
              value={plan}
              onChange={event => {
                const nextPlan = coercePlan(event.target.value);
                setPlan(nextPlan);
                localStorage.yearReadOption = nextPlan;
                selectReading(nextPlan, selectedDate);
              }}
              id="year-read-plan-select"
              aria-label="year-read-plan-select"
              disabled={loading}
              className="qt-source-select"
            />
          </div>
        </EuiFlexItem>
      </EuiFlexGroup>

      <div className="qt-date-navigation">
        <div className="qt-date-picker-wrap">
          <EuiDatePicker
            selected={moment(selectedDate, 'YYYY-MM-DD', true)}
            onChange={date => {
              if (date?.isValid()) selectReading(plan, date.format('YYYY-MM-DD'));
            }}
            filterDate={date => date.year() === currentYear}
            minDate={moment(`${currentYear}-01-01`, 'YYYY-MM-DD', true)}
            maxDate={moment(`${currentYear}-12-31`, 'YYYY-MM-DD', true)}
            dateFormat="YYYY-MM-DD"
            placeholder={translate('選擇日期')}
            disabled={loading}
            className="qt-date-picker"
            compressed
          />
        </div>

        <div className="qt-date-actions">
          <EuiToolTip content={translate('上一篇')}>
            <button
              type="button"
              className="qt-nav-action qt-nav-action--step"
              aria-label={translate('上一篇')}
              onClick={() => selectReading(plan, previousDate)}
              disabled={!previousDate || loading}
            >
              <span aria-hidden="true" className="qt-nav-arrow">←</span>
              <span className="qt-nav-label">{translate('上一篇')}</span>
            </button>
          </EuiToolTip>

          <EuiToolTip content={translate('下一篇')}>
            <button
              type="button"
              className="qt-nav-action qt-nav-action--step"
              aria-label={translate('下一篇')}
              onClick={() => selectReading(plan, nextDate)}
              disabled={!nextDate || loading}
            >
              <span className="qt-nav-label">{translate('下一篇')}</span>
              <span aria-hidden="true" className="qt-nav-arrow">→</span>
            </button>
          </EuiToolTip>

          <EuiToolTip content={translate('回到今天')}>
            <button
              type="button"
              className="qt-nav-action qt-nav-action--today"
              onClick={() => selectReading(plan, today)}
              disabled={selectedDate === today || loading}
            >
              {translate('今天')}
            </button>
          </EuiToolTip>
        </div>
      </div>
      <EuiSpacer size="s" />

      {loading ? (
        <div className="qt-player-loading">
          <EuiLoadingSpinner size="xl" />
        </div>
      ) : iframeSrc ? (
        <>
          <div className="qt-player-frame">
            <iframe
              id="ytplayer"
              className="qt-player-iframe"
              title="year-read-player"
              src={iframeSrc}
              frameBorder="0"
              allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
              allowFullScreen
            />
          </div>
          <EuiText size="s" className="qt-current-video-title">
            <p>
              {selectedDate} · {translate('第')} {dayNumber} {translate('天')} · {translate(bookName)}
            </p>
          </EuiText>
        </>
      ) : (
        <div className="qt-player-message">
          <EuiText>
            <p>{message || translate('抱歉，沒有找到可播放的內容。')}</p>
          </EuiText>
        </div>
      )}

      {!loading && (
        <div className="qt-history-section">
          <EuiText size="s">
            <strong>{translate('最近可播放')}</strong>
            <span className="qt-history-count">{recentDates.length}</span>
          </EuiText>
          <div className="qt-history-strip" role="list" aria-label={translate('最近可播放')}>
            {recentDates.map(date => {
              const reading = normalizeDaily(GetDailyVerse(getDayIndex(date), plan));
              return (
                <button
                  type="button"
                  role="listitem"
                  key={`${plan}-${date}`}
                  className={`qt-history-date${date === selectedDate ? ' is-selected' : ''}`}
                  onClick={() => selectReading(plan, date)}
                  title={reading.book || reading.id}
                >
                  {formatShortDate(date)}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
